import { appPathname } from "@/lib/opsBase";

export const MANAGER_TOKEN_KEY = "ttt_auth_token";
export const MANAGER_SESSION_EXPIRED_EVENT = "ttt:manager-session-expired";

const RETURN_TO_KEY = "ttt_auth_return_to";
const NOTICE_KEY = "ttt_auth_notice";

/*
 * The Team -> Ops handoff lived here: an `sso=1` marker plus an HttpOnly
 * parent-domain cookie redeemed at /api/ts/manage-session/claim. It existed
 * only because Ops sat on a separate origin from the hub and localStorage does
 * not cross origins, so the token could travel neither in storage nor in the
 * URL. Ops is at /ops on the hub's own origin now: the tile stores the token
 * directly and there is nothing left to claim. */

/**
 * Where to send someone after re-authenticating, as a *router-relative* path.
 *
 * Router-relative because the only consumer feeds it straight to `navigate()`,
 * and under Ops the router mounts with `/ops` as its basename — so a raw
 * `window.location.pathname` of `/ops/orders` was stored verbatim and came back
 * out as `/ops/ops/orders`. `appPathname()` strips the prefix the basename
 * re-adds; off Ops it is a no-op.
 *
 * This is reachable from a second tab: browsers clone sessionStorage into a tab
 * opened from a same-origin link, so a stale entry written here can be consumed
 * by a middle-clicked Ops tile on the Team home page.
 */
function currentManagerLocation() {
  if (typeof window === "undefined") return null;
  const pathname = appPathname();
  if (!pathname) return null;
  return `${pathname}${window.location.search || ""}${window.location.hash || ""}`;
}

function normalizeManagerReturnTo(returnTo = "") {
  if (typeof returnTo !== "string") return "/";

  const safePath = returnTo.trim();
  if (!safePath.startsWith("/") || safePath.startsWith("//")) return "/";
  if (safePath === "/") return "/";
  if (safePath.startsWith("/login")) return "/";
  return safePath;
}

export function setManagerReturnTo(returnTo) {
  if (typeof window === "undefined") return;
  const safe = normalizeManagerReturnTo(returnTo);
  if (safe === "/") {
    sessionStorage.removeItem(RETURN_TO_KEY);
    return;
  }

  sessionStorage.setItem(RETURN_TO_KEY, safe);
}

export function setManagerReturnToFromLocation(location = typeof window !== "undefined" ? window.location : null) {
  if (typeof window === "undefined" || !location) return;

  const pathname = String(location.pathname || "");
  const path = appPathname(pathname);
  if (!path || path === "/") {
    setManagerReturnTo("/");
    return;
  }
  const value = `${path}${location.search || ""}${location.hash || ""}`;
  setManagerReturnTo(value);
}

export function expireManagerSession() {
  if (typeof window === "undefined") return;

  const returnTo = currentManagerLocation();
  if (returnTo) {
    setManagerReturnTo(returnTo);
  }
  sessionStorage.setItem(NOTICE_KEY, "Your session expired. Please sign in again.");
  localStorage.removeItem(MANAGER_TOKEN_KEY);
  window.dispatchEvent(new Event(MANAGER_SESSION_EXPIRED_EVENT));
}

export function consumeManagerSessionNotice() {
  if (typeof window === "undefined") return "";
  const notice = sessionStorage.getItem(NOTICE_KEY) || "";
  sessionStorage.removeItem(NOTICE_KEY);
  return notice;
}

export function consumeManagerReturnTo() {
  if (typeof window === "undefined") return "/";
  const returnTo = normalizeManagerReturnTo(sessionStorage.getItem(RETURN_TO_KEY) || "/");
  sessionStorage.removeItem(RETURN_TO_KEY);
  return returnTo;
}
